import * as React from "react";
import {
  MultiSelect,
  Grid,
  TextInput,
  Button,
  Modal,
  Title,
  useMantineTheme,
  Text,
  Stack,
  Flex,
  Tooltip,
  ActionIcon,
} from "@mantine/core";

import { UseCaseBasicType, UseCaseType } from "@supportinitiative/common";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

import { UserContext } from "../../../misc/UserContext";
import { useUseCaseBasic } from "../../../hooks";
import { MultiSelectCreatable } from "../../../ui/MultiSelectCreatable";
import { getUseCaseDatasetData } from "../../../api";
import DataDisplay from "../DataDisplay";

export const escapeRegExp = (
  str: string // or better use 'escape-string-regexp' package
) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// TODO: Display minimal columns
// TODO: Detail data display modal
// TODO: Entry level update

function getIsMatch(filter: string[]) {
  const isMatch = (data: any) => {
    let found = filter.every((search) => {
      const re = new RegExp(escapeRegExp(search), "i");
      let item = Object.keys(data).find((column) => {
        return re.test(data[column]);
      });
      if (item) {
        return true;
      }
      return false;
    });
    return found;
  };
  return isMatch;
}

export default function DatasetData({
  entityId,
  id,
  recon,
  runId,
  runSeq,
  access,
  dataset,
  type,
  status,
  queryId,
  initialValues,
  fetchOnMount = false,
}: {
  entityId: string | null | undefined;
  id: string | null | undefined;
  recon: string | null | undefined;
  runId: string | null | undefined;
  runSeq: string | null | undefined;
  access: string | null | undefined;
  dataset: UseCaseBasicType["definition"]["recons"][number]["definition"]["datasets"][number];
  type: string | null | undefined;
  status: string | null | undefined;
  queryId: string | null | undefined;
  initialValues?: { data: { __id: number }[]; minId: number; maxId: number };
  fetchOnMount?: boolean;
}) {
  const { state: userState, dispatch: userStateDispatch } =
    React.useContext(UserContext);

  const { data: useCase } = useUseCaseBasic(entityId, id);

  const theme = useMantineTheme();

  // Fetching indicator to disble the next/prev buttons
  const [fetching, setFetching] = React.useState(false);

  // Min, max of the total set
  const [minId, setMinId] = React.useState<number | null>(
    initialValues && initialValues.minId ? initialValues.minId : -1
  );
  const [maxId, setMaxId] = React.useState<number | null>(
    initialValues && initialValues.maxId ? initialValues.maxId : -1
  );

  // Min, max of the items fetched
  const [currentMinId, setCurrentMinId] = React.useState<number | null>(
    initialValues && initialValues.data.length > 0
      ? initialValues.data[0].__id
      : -1
  );
  const [currentMaxId, setCurrentMaxId] = React.useState<number | null>(
    initialValues && initialValues.data.length > 0
      ? initialValues.data[initialValues.data.length - 1].__id
      : -1
  );

  // Initial data
  const [data, setData] = React.useState<{ __id: number }[]>(
    initialValues && initialValues.data && initialValues.data.length > 0
      ? initialValues.data
      : []
  );

  // Front end filter
  const [filterList, setFilterList] = React.useState<
    { value: string; label: string }[]
  >([]);
  const [filter, setFilter] = React.useState<string[]>([]);

  // Filtered data
  const [filteredData, setFilteredData] = React.useState<{ __id: number }[]>(
    initialValues && initialValues.data && initialValues.data.length > 0
      ? initialValues.data
      : []
  );

  // Backend search
  const [search, setSearch] = React.useState("");

  const [selection, setSelection] = React.useState<string[]>([]);

  const [display, setDisplay] = React.useState<string>("none");

  // Reset data on change of the URL
  React.useEffect(() => {
    // console.log("fetching on mount", fetchOnMount);
    if (fetchOnMount) {
      const getData = async () => {
        let values = await getUseCaseDatasetData(
          entityId,
          id,
          recon,
          dataset.id,
          runId,
          runSeq,
          access,
          type,
          status,
          queryId,
          "next",
          "20",
          currentMinId,
          currentMaxId,
          minId,
          maxId,
          search
        );
        setData(values.data);

        // Filter and set
        const newFilteredData = values.data.filter(getIsMatch(filter));
        setFilteredData(newFilteredData);

        if (values.data && values.data.length > 0) {
          setCurrentMinId(values.data[0].__id);
          setCurrentMaxId(values.data[values.data.length - 1].__id);
        }
        setMinId(values.minId);
        setMaxId(values.maxId);
        setFetching(false);
      };
      getData();
    } else {
      setData(
        initialValues && initialValues.data && initialValues.data.length > 0
          ? initialValues.data
          : []
      );

      setCurrentMinId(
        initialValues && initialValues.data.length > 0
          ? initialValues.data[0].__id
          : -1
      );
      setCurrentMaxId(
        initialValues && initialValues.data.length > 0
          ? initialValues.data[initialValues.data.length - 1].__id
          : -1
      );

      setMinId(initialValues && initialValues.minId ? initialValues.minId : -1);
      setMaxId(initialValues && initialValues.maxId ? initialValues.maxId : -1);

      const newFilteredData =
        initialValues && initialValues.data && initialValues.data.length > 0
          ? initialValues.data.filter(getIsMatch(filter))
          : [];
      setFilteredData(newFilteredData);
    }
  }, [entityId, id, recon, runId, runSeq, access, type, queryId]);

  // Change on filter change. No need to reset the min, max ids. This is only a JS level filter
  React.useEffect(() => {
    const newFilteredData = data.filter(getIsMatch(filter));
    setFilteredData(newFilteredData);
  }, [filter]);

  return (
    <Stack mt="xs" h="100%" p="xs" bg="#FAF7F3" mb="lg">
      <Flex align={"center"} gap={theme.spacing.xs}>
        <Title
          order={5}
          py={0}
          px={theme.spacing.sm}
          style={{
            borderLeft: `4px solid ${theme.colors.emerald[0]}`,
          }}
        >
          {dataset.name}
        </Title>

        <Tooltip label="Previous">
          <ActionIcon
            size="sm"
            variant="transparent"
            color="gray"
            ml="xs"
            onClick={async () => {
              setFetching(true);
              const values = await getUseCaseDatasetData(
                entityId,
                id,
                recon,
                dataset.id,
                runId,
                runSeq,
                access,
                type,
                status,
                queryId,
                "prev",
                "20",
                currentMinId,
                currentMaxId,
                minId,
                maxId,
                search
              );
              setData(values.data);

              // Filter and set
              const newFilteredData = values.data.filter(getIsMatch(filter));
              setFilteredData(newFilteredData);

              if (values.data && values.data.length > 0) {
                setCurrentMinId(values.data[0].__id);
                setCurrentMaxId(values.data[values.data.length - 1].__id);
              }
              setMinId(values.minId);
              setMaxId(values.maxId);
              setFetching(false);
            }}
            disabled={currentMinId === minId || fetching}
          >
            <IconChevronLeft
              style={{ width: "70%", height: "70%" }}
              stroke={2}
            />
          </ActionIcon>
        </Tooltip>

        <Tooltip label="Next">
          <ActionIcon
            size="sm"
            variant="transparent"
            color="gray"
            ml="xs"
            onClick={async () => {
              setFetching(true);
              const values = await getUseCaseDatasetData(
                entityId,
                id,
                recon,
                dataset.id,
                runId,
                runSeq,
                access,
                type,
                status,
                queryId,
                "next",
                "20",
                currentMinId,
                currentMaxId,
                minId,
                maxId,
                search
              );
              setData(values.data);

              // Filter and set
              const newFilteredData = values.data.filter(getIsMatch(filter));
              setFilteredData(newFilteredData);

              if (values.data && values.data.length > 0) {
                setCurrentMinId(values.data[0].__id);
                setCurrentMaxId(values.data[values.data.length - 1].__id);
              }
              setMinId(values.minId);
              setMaxId(values.maxId);
              setFetching(false);
            }}
            disabled={currentMaxId === maxId || fetching}
          >
            <IconChevronRight
              style={{ width: "70%", height: "70%" }}
              stroke={2}
            />
          </ActionIcon>
        </Tooltip>

        <Grid align="flex-start" gutter={"xs"} style={{ flexGrow: 1 }}>
          <Grid.Col span={5}>
            <MultiSelectCreatable
              size="xs"
              data={filterList}
              placeholder="Filter"
              onChange={(value) => {
                setFilter(value);
              }}
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <TextInput
              size="xs"
              value={search}
              placeholder="Query (ex: column1 = 'ABC' AND column2 IN ('1','2'))"
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <Button variant="default" fullWidth size="xs">
              Fetch
            </Button>
          </Grid.Col>
        </Grid>
      </Flex>
      <DataDisplay
        data={filteredData} // Pass as array
        table="details"
        definition={dataset.columns}
        links={
          dataset.id === "EVENT"
            ? [
                {
                  name: "__matchids",
                  split: true,
                  seperator: ",",
                  link: `/app/usecases/view/match?entityId=${entityId}&id=${id}&recon=${recon}&runId=${runId}&runSeq=${runSeq}&access=${access}&matchId=\${__value}`,
                },
                {
                  name: "details",
                  displayValue: "Details",
                  link: `/app/usecases/view/event?entityId=${entityId}&id=${id}&recon=${recon}&runId=${runId}&runSeq=${runSeq}&access=${access}&eventId=\${__id}`,
                },
              ]
            : [
                {
                  name: "__matchids",
                  split: true,
                  seperator: ",",
                  link: `/app/usecases/view/match?entityId=${entityId}&id=${id}&recon=${recon}&runId=${runId}&runSeq=${runSeq}&access=${access}&matchId=\${__value}`,
                },
              ]
        }
        enableSelection={false}
        // ignoreColumns={dataset.id === "EVENT" ? ["details"] : []}
      />
    </Stack>
  );
}
